import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { JiraProject, JiraProjectsPage, Project, Task } from '@/types';

/* eslint-disable @typescript-eslint/no-explicit-any */

interface ProjectsState {
  activeComponent: string;
  projects: Project[];
  visibleJiraProjects: JiraProject[];
  selectedJiraProject: JiraProject | Record<string, never>;
  jiraProjects: JiraProjectsPage;
  allJiraProjects: JiraProjectsPage;
  selectedProject: Project | Record<string, never>;
  selectedProjectTasks: Task[];
  selectedTask: Task | Record<string, never>;

  updateProjects: (projects: Project[]) => void;
  updateJiraProjects: (jiraProjects: JiraProjectsPage) => void;
  updateVisibleJiraProjects: (visibleJiraProjects: JiraProject[]) => void;
  updateSelectedJiraProject: (jiraProject: JiraProject) => void;
  updateSelectedProject: (project: Project | Record<string, never>) => void;
  updateSelectedProjectTasks: (tasks: Task[]) => void;
  updateSelectedTask: (task: Task | Record<string, never>) => void;
  setActiveComponent: (componentName: string) => void;
  initializeAllJiraProjects: (firstPageData: JiraProjectsPage) => void;
  appendToAllJiraProjects: (newPageData: JiraProjectsPage) => void;
  setAllJiraProjects: (
    allProjectsData: Partial<JiraProjectsPage> & { values: JiraProject[] }
  ) => void;
  clearAllJiraProjects: () => void;
}

const useProjectsStore = create<ProjectsState>()(
  devtools(
    (set) => ({
      activeComponent: 'ProjectBody',
      projects: [],
      visibleJiraProjects: [],
      selectedJiraProject: {},
      jiraProjects: {
        values: [],
        startAt: 0,
        maxResults: 10,
        total: 0,
        isLast: true,
        self: '',
        nextPage: '',
      },
      allJiraProjects: {
        values: [],
        startAt: 0,
        maxResults: 10,
        total: 0,
        isLast: true,
        self: '',
        nextPage: '',
      },
      selectedProject: {},
      selectedProjectTasks: [],
      selectedTask: {},

      // =====================================
      updateProjects: (projects) => set({ projects }, false, 'updateProjects'),
      updateJiraProjects: (jiraProjects) =>
        set({ jiraProjects }, false, 'updateJiraProjects'),

      updateVisibleJiraProjects: (visibleJiraProjects) =>
        set({ visibleJiraProjects }, false, 'updateVisibleJiraProjects'),
      updateSelectedJiraProject: (jiraProject) =>
        set(
          { selectedJiraProject: jiraProject },
          false,
          'updateSelectedJiraProject'
        ),
      updateSelectedProject: (project) =>
        set({ selectedProject: project }, false, 'updateSelectedProject'),
      updateSelectedProjectTasks: (selectedProjectTasks) =>
        set({ selectedProjectTasks }, false, 'updateSelectedProjectTasks'),
      updateSelectedTask: (task) =>
        set({ selectedTask: task }, false, 'updateSelectedTask'),
      setActiveComponent: (componentName) =>
        set({ activeComponent: componentName }, false, 'setActiveComponent'),

      // AllJiraProjects methods
      initializeAllJiraProjects: (firstPageData) =>
        set(
          {
            allJiraProjects: {
              values: [...firstPageData.values],
              startAt: 0,
              maxResults: firstPageData.maxResults,
              total: firstPageData.total,
              isLast: firstPageData.isLast,
              self: firstPageData.self || '',
              nextPage: firstPageData.nextPage || '',
            },
          },
          false,
          'initializeAllJiraProjects'
        ),

      appendToAllJiraProjects: (newPageData) =>
        set(
          (state) => ({
            allJiraProjects: {
              ...state.allJiraProjects,
              values: [...state.allJiraProjects.values, ...newPageData.values],
              total: newPageData.total,
              isLast: newPageData.isLast,
              nextPage: newPageData.nextPage || '',
            },
          }),
          false,
          'appendToAllJiraProjects'
        ),

      setAllJiraProjects: (allProjectsData) =>
        set(
          {
            allJiraProjects: {
              values: [...allProjectsData.values],
              startAt: allProjectsData.startAt || 0,
              maxResults: allProjectsData.maxResults || 10,
              total: allProjectsData.total || allProjectsData.values.length,
              isLast:
                allProjectsData.isLast !== undefined
                  ? allProjectsData.isLast
                  : true,
              self: allProjectsData.self || '',
              nextPage: allProjectsData.nextPage || '',
            },
          },
          false,
          'setAllJiraProjects'
        ),

      clearAllJiraProjects: () =>
        set(
          {
            allJiraProjects: {
              values: [],
              startAt: 0,
              maxResults: 10,
              total: 0,
              isLast: true,
              self: '',
              nextPage: '',
            },
          },
          false,
          'clearAllJiraProjects'
        ),
    }),
    { name: 'ProjectsStore' }
  )
);

export default useProjectsStore;
